import React, { useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors,
} from "@dnd-kit/core";
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Pencil, Check, X } from "lucide-react";

const LS_KEY = (role) => `baim_dashboard_order_${role}`;

/** Fetch the saved section order for this dashboard role.
 *  Falls back to localStorage for instant load, then reconciles with DB. */
export function useDashboardOrder(role, defaultOrder) {
  const [order, setOrder] = useState(() => {
    try {
      const cached = localStorage.getItem(LS_KEY(role));
      if (cached) {
        const parsed = JSON.parse(cached);
        if (Array.isArray(parsed)) return reconcileOrder(parsed, defaultOrder);
      }
    } catch {}
    return defaultOrder;
  });

  useEffect(() => {
    let alive = true;
    api.get("/user/preferences/dashboard").then((r) => {
      if (!alive) return;
      const saved = r.data?.dashboard_orders?.[role];
      if (Array.isArray(saved) && saved.length > 0) {
        const merged = reconcileOrder(saved, defaultOrder);
        setOrder(merged);
        try { localStorage.setItem(LS_KEY(role), JSON.stringify(merged)); } catch {}
      }
    }).catch(() => {});
    return () => { alive = false; };
    // eslint-disable-next-line
  }, [role]);

  const save = useCallback(async (newOrder) => {
    try { localStorage.setItem(LS_KEY(role), JSON.stringify(newOrder)); } catch {}
    try {
      await api.put("/user/preferences/dashboard", { dashboard_role: role, section_order: newOrder });
      toast.success("Layout saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save error");
    }
  }, [role]);

  const reset = useCallback(async () => {
    try { localStorage.removeItem(LS_KEY(role)); } catch {}
    setOrder(defaultOrder);
    try {
      await api.put("/user/preferences/dashboard", { dashboard_role: role, section_order: [] });
      toast.success("Default layout restored");
    } catch {}
  }, [role, defaultOrder]);

  return { order, setOrder, save, reset };
}

function reconcileOrder(saved, defaults) {
  // Keep only known ids from saved, then append any new defaults at the end.
  const validIds = new Set(defaults);
  const kept = saved.filter((id) => validIds.has(id));
  const missing = defaults.filter((id) => !kept.includes(id));
  return [...kept, ...missing];
}

function SortableSection({ id, editMode, children, label }) {
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 50 : "auto",
  };
  return (
    <div ref={setNodeRef} style={style} data-testid={`dash-section-${id}`} className="relative">
      {editMode && (
        <div className="absolute -left-4 top-1/2 -translate-y-1/2 z-10 flex flex-col items-center gap-1">
          <button
            {...attributes}
            {...listeners}
            data-testid={`drag-handle-${id}`}
            className="p-1.5 bg-white border border-brand-navy/30 rounded-md shadow-sm cursor-grab active:cursor-grabbing hover:bg-brand-cream-200 text-brand-navy"
            title={`Déplacer « ${label || id} »`}
            aria-label={`Déplacer ${label || id}`}
          >
            <GripVertical size={14} />
          </button>
        </div>
      )}
      <div className={editMode ? "ring-2 ring-brand-navy/20 ring-offset-2 ring-offset-[#F8F3E8] rounded-lg transition-all" : ""}>
        {children}
      </div>
    </div>
  );
}

/**
 * DraggableDashboard — wraps a list of sections and lets the user reorder them in edit mode.
 * sections: [{ id: string, label: string, render: () => ReactNode }]
 */
export default function DraggableDashboard({ role, sections }) {
  const defaultOrder = useMemo(() => sections.map((s) => s.id), [sections]);
  const { order, setOrder, save, reset } = useDashboardOrder(role, defaultOrder);
  const [editMode, setEditMode] = useState(false);
  const [draftOrder, setDraftOrder] = useState(order);

  useEffect(() => { setDraftOrder(order); }, [order]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setDraftOrder((items) => {
      const oldIndex = items.indexOf(active.id);
      const newIndex = items.indexOf(over.id);
      return arrayMove(items, oldIndex, newIndex);
    });
  };

  const enterEdit = () => { setDraftOrder(order); setEditMode(true); };
  const cancelEdit = () => { setDraftOrder(order); setEditMode(false); };
  const saveEdit = async () => {
    setOrder(draftOrder);
    await save(draftOrder);
    setEditMode(false);
  };

  const renderOrder = editMode ? draftOrder : order;
  const sectionMap = useMemo(() => Object.fromEntries(sections.map((s) => [s.id, s])), [sections]);

  return (
    <>
      {/* Edit toolbar */}
      <div className="flex items-center justify-end gap-2 mb-6" data-testid="dashboard-edit-toolbar">
        {!editMode && (
          <button
            onClick={enterEdit}
            data-testid="dashboard-customize-btn"
            className="inline-flex items-center gap-2 px-3 h-9 text-xs font-medium text-brand-navy border border-brand-navy/30 rounded-md hover:bg-brand-cream-200 transition-colors"
          >
            <Pencil size={13} /> Personnaliser
          </button>
        )}
        {editMode && (
          <>
            <div className="text-xs text-brand-navy-500 font-mono mr-2">Drag the handles to reorder</div>
            <button
              onClick={reset}
              data-testid="dashboard-reset-btn"
              className="inline-flex items-center gap-2 px-3 h-9 text-xs font-medium text-zinc-600 hover:text-zinc-900 border border-zinc-200 rounded-md hover:bg-zinc-50"
            >
              Réinitialiser
            </button>
            <button
              onClick={cancelEdit}
              data-testid="dashboard-cancel-btn"
              className="inline-flex items-center gap-2 px-3 h-9 text-xs font-medium text-zinc-600 hover:text-zinc-900 border border-zinc-200 rounded-md hover:bg-zinc-50"
            >
              <X size={13} /> Cancel
            </button>
            <button
              onClick={saveEdit}
              data-testid="dashboard-save-btn"
              className="inline-flex items-center gap-2 px-3 h-9 bg-brand-navy text-white text-xs font-semibold rounded-md hover:bg-brand-navy-600"
            >
              <Check size={13} /> Valider
            </button>
          </>
        )}
      </div>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={renderOrder} strategy={verticalListSortingStrategy}>
          <div className="space-y-10">
            {renderOrder.map((id) => {
              const s = sectionMap[id];
              if (!s) return null;
              return (
                <SortableSection key={id} id={id} editMode={editMode} label={s.label}>
                  {s.render()}
                </SortableSection>
              );
            })}
          </div>
        </SortableContext>
      </DndContext>
    </>
  );
}
